/**
 * Keeping a signed-in console signed in.
 *
 * THE BUG. `SESSION_TOKEN_TTL_SECONDS` is 28800 — eight hours. `POST
 * /auth/refresh` has existed since the auth service was written, sets fresh
 * session and refresh cookies, and the console never called it. So a tech who
 * signed in at 08:00 was hard signed out at 16:00, mid-conversation, with no
 * warning and no way back but a full re-auth.
 *
 * It is worse alongside the SSE reconnect: a dropped feed that 401s would
 * otherwise retry against an expired session forever, showing "reconnecting"
 * to someone who is actually signed out.
 *
 * THE SHAPE. Reactive, not scheduled. The first request after expiry gets a
 * 401, this refreshes once, and the request is retried — the person sees
 * nothing. A timer would mean guessing at an expiry the client cannot see (the
 * cookies are httpOnly) and refreshing needlessly all day.
 *
 * SINGLE FLIGHT, and it matters more than usual here. Rotation revokes the
 * whole family when a rotated-out token is replayed. A console fires several
 * requests at once (threads, presence, config), so without this they would all
 * 401 together, all refresh together, and all but one would present a token the
 * winner had just rotated — signing the user out of everything, which is far
 * worse than the expiry being fixed. Migration 004's grace window covers the
 * cross-TAB case this cannot see; this covers within a tab.
 */

/**
 * Why this is three states and not a boolean.
 *
 * The original code read `if (res.ok) return true` and then called
 * `onSignedOut()` for EVERYTHING else — despite the comment beside it saying
 * "401 here means". A 502 while a container restarts, a 503, a gateway
 * timeout: all of them threw the user to the sign-in screen with a session
 * that was still perfectly valid. Refreshing the page put them straight back,
 * which is the tell. Every deploy did this to whoever was working.
 *
 * `refused` is the server saying no. `unavailable` is not reaching the server
 * at all, which says nothing about whether the session is good.
 */
export type RefreshOutcome = 'ok' | 'refused' | 'unavailable'

let inFlight: Promise<RefreshOutcome> | null = null

export interface RefreshDeps {
  fetchImpl?: typeof fetch
  /** Resolves the auth origin. Same base the ops client uses. */
  resolveBase: () => Promise<string>
  /**
   * Called ONLY when the server refused the refresh token — expired, revoked
   * or replayed. Never called because the server could not be reached.
   */
  onSignedOut?: () => void
}

/**
 * Refresh the session, at most once concurrently.
 *
 * `ok` means the caller may retry its request. Never throws: a refresh failing
 * is an ordinary outcome, and a throw here would surface as a crash in
 * whatever request happened to trigger it.
 */
export function refreshSession(deps: RefreshDeps): Promise<RefreshOutcome> {
  if (inFlight) return inFlight

  const doFetch = deps.fetchImpl ?? fetch
  inFlight = (async () => {
    try {
      const base = await deps.resolveBase()
      const res = await doFetch(`${base}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        // The refresh cookie is scoped to /auth/refresh and carries the token;
        // an empty body is correct. The route accepts a body token too, for
        // non-browser callers that have no cookie jar.
        body: '{}',
      })
      if (res.ok) return 'ok'

      // 401/403 is the server refusing the token — expired, revoked, or
      // replayed. That is a real sign-out, and retrying would only revoke
      // more of the family.
      if (res.status === 401 || res.status === 403) {
        deps.onSignedOut?.()
        return 'refused'
      }

      // Anything else — 502 and 503 while a container restarts, 504, a 500 —
      // is the server being unreachable or broken, which says NOTHING about
      // the session. Signing someone out for it is both wrong and destructive:
      // they lose what they were typing to fix a problem that fixes itself in
      // seconds.
      return 'unavailable'
    } catch {
      // Network failure, not an auth failure. Same reasoning: throwing someone
      // to the sign-in page because their wifi blinked would lose whatever
      // they were typing. (Andrew is on Starlink; this is not hypothetical.)
      return 'unavailable'
    } finally {
      inFlight = null
    }
  })()

  return inFlight
}

/**
 * Wrap a fetch so any 401 triggers one refresh and one retry.
 *
 * Retries exactly once. A second 401 after a successful refresh means the
 * request is being refused for a reason a new session will not fix — an
 * ownership gate, say — and retrying it in a loop would hammer the service
 * while telling the user nothing.
 */
export function withSessionRefresh(inner: typeof fetch, deps: RefreshDeps): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const res = await inner(input, init)
    if (res.status !== 401) return res

    // Compared against 'ok' explicitly. A truthiness check would pass for
    // 'refused' and 'unavailable' alike — every outcome is a non-empty string
    // — and retry a request that cannot succeed.
    const outcome = await refreshSession(deps)
    if (outcome !== 'ok') return res

    return inner(input, init)
  }
}

/** Test seam: clears the single-flight latch between cases. */
export function resetRefreshStateForTests(): void {
  inFlight = null
}
